import {
  DealItemUpdateInput,
  useUpdateDealItemsV2Mutation,
} from '@/lib/graphql/generated/graphql';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import { trackEvent } from '@/lib/mixpanel';
import { type UndoEntryItem } from '@/states/undo-stack.state';

export type UpdateDealItemsOptions = {
  /** Ne pas empiler CTRL+Z (tests ou flux spécifiques). */
  skipUndo?: boolean;
};

// 1. Liste des clés « prix » à remultiplier par 100
const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'shippingPrice',
  'eeePrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

function transform(item: DealItemUpdateInput): DealItemUpdateInput {
  const payload: DealItemUpdateInput = { ...item };

  // ×100 sur les prix
  priceKeys.forEach((key: PriceKey) => {
    const v = item[key];
    if (typeof v === 'number') payload[key] = Math.round(v * 100);
  });

  // % → fraction
  if (item.chooseDiscount != null) {
    payload.chooseDiscount = item.chooseDiscount / 100;
  }

  if (item.marginPercentage != null) {
    payload.marginPercentage = item.marginPercentage / 100;
  }

  return payload;
}

export const useUpdateDealItems = (dealId: string) => {
  const [updateDealItemsMutation, { loading, error }] =
    useUpdateDealItemsV2Mutation();
  const {
    pushUndoEntry,
    canUndo,
    canRedo,
    undo,
    redo,
    invalidateUndoForItems,
  } = useUndoDealItems(dealId);

  const recordUndoFromMutationResult = (
    result: Awaited<ReturnType<typeof updateDealItemsMutation>>,
    options?: UpdateDealItemsOptions
  ) => {
    if (options?.skipUndo) return;
    const historyItems = extractHistoryItems(result);
    if (historyItems.length > 0) {
      pushUndoEntry(historyItems);
    }
  };

  const trackUpdateResult = (
    items: DealItemUpdateInput[],
    result: Awaited<ReturnType<typeof updateDealItemsMutation>>
  ) => {
    const fieldNames = extractFieldNames(items);
    const response = result.data?.updateDealItemsV2;
    if (response?.__typename === 'MutationUpdateDealItemsSuccess') {
      trackEvent('DEAL_ITEM_CELL_EDIT_SAVED', {
        dealId,
        fieldNames,
        itemCount: items.length,
      });
    } else {
      trackEvent('DEAL_ITEM_CELL_EDIT_FAILED', {
        dealId,
        fieldNames,
        itemCount: items.length,
        errorCode:
          response?.__typename === 'DomainException'
            ? response.code
            : 'unknown_error',
      });
    }
  };

  const trackThrownError = (items: DealItemUpdateInput[]) => {
    trackEvent('DEAL_ITEM_CELL_EDIT_FAILED', {
      dealId,
      fieldNames: extractFieldNames(items),
      itemCount: items.length,
      errorCode: 'network_error',
    });
  };

  /** Met à jour un seul produit */
  const updateProduct = async (
    item: DealItemUpdateInput,
    options?: UpdateDealItemsOptions
  ) => {
    const payload = transform(item);
    try {
      const result = await updateDealItemsMutation({
        variables: { dealId, items: [payload] },
      });
      trackUpdateResult([item], result);
      recordUndoFromMutationResult(result, options);
      return result;
    } catch (err) {
      trackThrownError([item]);
      throw err;
    }
  };

  /** Met à jour plusieurs produits en une seule mutation */
  const updateProducts = async (
    items: DealItemUpdateInput[],
    options?: UpdateDealItemsOptions
  ) => {
    const payloads = items.map(transform);
    try {
      const result = await updateDealItemsMutation({
        variables: { dealId, items: payloads },
      });
      trackUpdateResult(items, result);
      recordUndoFromMutationResult(result, options);
      return result;
    } catch (err) {
      trackThrownError(items);
      throw err;
    }
  };

  return {
    updateProduct,
    updateProducts,
    loading,
    error,
    canUndo,
    canRedo,
    undo,
    redo,
    invalidateUndoForItems,
  };
};

/** Extrait les entrées undo à partir du tableau `updates` d’une mutation deal items réussie. */
export function historyItemsFromMutationUpdates(
  updates: ReadonlyArray<{
    id: string;
    previousHistoryId: string | null | undefined;
    currentHistoryId: string | null | undefined;
  }>
): UndoEntryItem[] {
  return updates
    .filter((u) => u.previousHistoryId != null && u.currentHistoryId != null)
    .map((u) => ({
      dealItemId: u.id,
      previousHistoryId: u.previousHistoryId!,
      currentHistoryId: u.currentHistoryId!,
    }));
}

export function extractHistoryItems(
  result: Awaited<
    ReturnType<ReturnType<typeof useUpdateDealItemsV2Mutation>[0]>
  >
): UndoEntryItem[] {
  const response = result.data?.updateDealItemsV2;
  if (response?.__typename !== 'MutationUpdateDealItemsSuccess') return [];
  return historyItemsFromMutationUpdates(response.updates);
}

function extractFieldNames(items: DealItemUpdateInput[]): string[] {
  const fields = new Set<string>();
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (key !== 'id') fields.add(key);
    }
  }
  return Array.from(fields);
}
